import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges, OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { IneligibilityService } from 'src/app/modules/iep/iep-document/services/ineligibility-service';
import { SelectedStaff } from 'src/app/modules/pwn/interfaces/source-staff-payload';
import { PwnType } from 'src/app/modules/pwn/shared/enums/pwn-type';
import { StaffContact } from 'src/app/modules/referrals/interfaces/staff-contact';
import { StaffSearchService } from 'src/app/services/staff-search.service';
import { ReferenceStore } from 'src/app/stores/reference.store';
import { DropdownOption } from '../../interfaces/dropdown-option';
import { StaffInfo } from '../../interfaces/staff-info';
import {
  validateEmail,
  validateName,
  validatePhoneNumberMaskedTextBox,
  validateStaffId,
} from '../../validators/validator';

@Component({
  selector: 'app-staff-search',
  templateUrl: './staff-search.component.html',
  styleUrls: ['./staff-search.component.scss'],
  standalone: false,
})
export class StaffSearchComponent
  implements OnChanges, AfterViewInit, OnDestroy, OnInit {
  gridData: StaffInfo[];
  selectedStaff: SelectedStaff;
  @Input() disableSearch: boolean = false;
  @Input() showAddress: boolean;
  @Input() showSchool: boolean;
  @Input() showEmail: boolean = true;
  @Input() showSelectedStaffInfo: boolean = true;
  @Input() isAssistanceContact: boolean;
  @Input() readOnly: boolean = false;
  @Input() isOtAssessment: boolean = false;
  @Input() initialStaff: SelectedStaff | null = null;
  @Input() phoneLabel: string = 'Phone *';
  @Input() emailLabel: string = 'Email Address *';
  @Output() selectedStaffChange: EventEmitter<SelectedStaff> =
    new EventEmitter<StaffContact>();
  @Output() formValues: EventEmitter<StaffInfo> = new EventEmitter<StaffInfo>();
  @Output() selectedStaffFormStatus: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Input() comparableService: StaffInfo;
  @Input() otService: StaffInfo;
  loadingPanelVisible: boolean = false;
  states: DropdownOption[];
  private destroySubject: Subject<void> = new Subject<void>();
  public mask: string = '(999) 000-00-00-00';
  selectedStaffForm!: FormGroup;
  showSearchError: boolean = false;
  isSearchVisible: boolean = true;
  @Input() pwnType: PwnType;
  @Input() isAAPackage: boolean;
  @Input() useFullName: boolean;
  readonly select: string = 'Select';
  readonly reselect: string = 'Reselect';
  @Output() resetSearchChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    public fb: FormBuilder,
    private staffService: StaffSearchService,
    private referenceStore: ReferenceStore,
    private ineligibilityService: IneligibilityService,
    private router: Router
  ) {
    this.#createRtiForm();
    this.referenceStore.state$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((res: DropdownOption[]) => {
        this.states = res;
      });
    // Reset search error when the user types in any field
    this.staffForm.valueChanges.subscribe(() => {
      this.showSearchError = false;
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disableSearch && this.disableSearch !== undefined) {
      if (this.disableSearch) {
        this.staffForm.disable();
        this.selectedStaffForm.disable();
      } else {
        this.staffForm.enable();
        this.selectedStaffForm.enable();
      }
    }

    if (
      this.pwnType === PwnType.NoticeOfIR ||
      this.pwnType === PwnType.Refusal
    ) {
      this.selectedStaffForm.get('phone')?.setValidators([Validators.required]);
      this.selectedStaffForm.get('phone')?.updateValueAndValidity();
    } else {
      this.selectedStaffForm
        .get('phone')
        ?.removeValidators([Validators.required]);
      this.selectedStaffForm.get('phone')?.updateValueAndValidity();
    }
    if (this.comparableService) {
      if (this.comparableService.fullName || this.comparableService.email ||
        this.comparableService.phone || this.comparableService.city ||
        this.comparableService.state || this.comparableService.zip) {
        const data: SelectedStaff = {
          fullName: this.comparableService?.fullName?.trim() || '',
          streetAddress1: this.comparableService.address1?.trim() || '',
          city: this.comparableService?.city?.trim() || '',
          state: this.comparableService?.state || '',
          zip: this.comparableService?.zip?.trim() || '',
          phone: this.comparableService?.phone || '',
          email: this.comparableService?.email || '',
        };
        this.selectedStaff = data;
      }

      if (this.selectedStaff) this.patchSelectedStaffToForm();
    }

    if (this.otService) {
      const data: StaffInfo = {
        fullName: this.otService?.fullName,
        streetAddress1: this.otService.streetAddress1?.trim() || '',
        city: this.otService?.city?.trim() || '',
        state: this.otService?.state || '',
        zip: this.otService?.zip?.trim() || '',
        phone: this.otService?.phone || '',
        email: this.otService?.email || '',
      };
      this.selectedStaff = data;

      if (this.selectedStaff) this.patchSelectedStaffToForm();
    }

    if (changes.initialStaff && this.initialStaff) {
      this.selectedStaff = this.initialStaff;
      this.patchSelectedStaffToForm();
      this.setValidators();
    }
  }

  staffForm: FormGroup = this.fb.group({
    firstName: ['', validateName(false)],
    lastName: ['', validateName(false)],
    fullName: [''],
    loginId: ['', validateStaffId(false)],
  });

  #createRtiForm(): void {
    this.selectedStaffForm = this.fb.group({
      fullName: [''],
      school: [''],
      streetAddress1: [''],
      streetAddress2: [''],
      city: [''],
      state: [null],
      zip: [''],
      workPhone: [''],
      phone: [''],
      email: [''],
      title: [''],
    });
  }

  onStaffSearch(): void {
    this.showSearchError = this.useFullName
      ? !this.staffForm.value.fullName && !this.staffForm.value.loginId
      : !this.staffForm.value.firstName &&
      !this.staffForm.value.lastName &&
      !this.staffForm.value.loginId;

    if (this.showSearchError) {
      this.staffForm.setErrors({ incorrect: true });
      return;
    } else {
      this.loadingPanelVisible = true;
      const searchPayload: {
        firstName?: string;
        lastName?: string;
        fullName?: string;
        loginId?: string;
      } = { ...this.staffForm.value };

      if (this.useFullName && this.staffForm.value.fullName) {
        const [fName, ...lNameParts]: string[] = this.staffForm.value.fullName
          .trim()
          .split(' ');
        searchPayload.firstName = fName;
        searchPayload.lastName = lNameParts.join(' ');
      }
      this.staffService.getStaffData(searchPayload).subscribe({
        next: (res: StaffInfo[]) => {
          this.gridData = res;
          this.loadingPanelVisible = false;
          if (this.gridData.length) {
            this.gridData.map((staff: StaffInfo) => {
              staff.fullName = `${staff.firstName} ${staff.lastName}`;
              staff.phone = staff.workPhone; // Ensure phone property is set
            });
          }
        },
        error: () => {
          this.loadingPanelVisible = false;
        },
      });
    }
    // this.selectedStaffForm.statusChanges.subscribe(() => {
    //   this.selectedStaffFormStatus.emit(this.selectedStaffForm.valid);
    // });
  }

  onselectStaff(index: number): void {
    this.isOtAssessment = false;
    this.selectedStaff = this.gridData[index];
    this.patchSelectedStaffToForm();
    this.formValues.emit(this.staffForm.value);

    this.gridData = undefined;

    if (this.isAssistanceContact) {
      this.ineligibilityService.getSelectedStaffData(this.selectedStaff, true);
    } else {
      this.ineligibilityService.getSelectedStaffData(this.selectedStaff, false);
    }
    this.setValidators();
    this.selectedStaffChange.next(this.selectedStaff);
    this.isSearchVisible = true;
  }

  patchSelectedStaffToForm(): void {
    const EMAIL_CONTROL: AbstractControl = this.selectedStaffForm.get('email');
    EMAIL_CONTROL.addValidators([Validators.required]);
    EMAIL_CONTROL.updateValueAndValidity();

    this.selectedStaffForm.patchValue({
      fullName: this.selectedStaff?.fullName || '',
      school:
        this.selectedStaff?.locationName || this.selectedStaff?.school || '',
      streetAddress1: this.fullAddress?.trim() || '',
      streetAddress2: this.selectedStaff?.streetAddress2?.trim() || '',
      city: this.selectedStaff?.city?.trim() || '',
      state: this.selectedStaff?.stateName || '',
      zip: this.selectedStaff?.zip?.trim() || '',
      workPhone: this.selectedStaff?.phone || '',
      phone: this.selectedStaff?.phone || '',
      email: this.selectedStaff?.email || '',
      title: this.selectedStaff?.title || '',
    });
    this.setValidators();
    this.isSearchVisible = true;
  }
  get fullAddress(): string {
    if (this.selectedStaff === null) {
      return '';
    }
    if (this.isOtAssessment && this.otService) {
      return [
        this.selectedStaff.streetAddress1,
        this.selectedStaff.streetAddress2 ?? '',
      ]
        .filter(Boolean)
        .join(', ');
    } else {
      return [
        this.selectedStaff.streetAddress1,
        this.selectedStaff.streetAddress2 ?? '',
        this.selectedStaff.city,
        this.selectedStaff.stateName,
        this.selectedStaff.zip,
      ]
        .filter(Boolean)
        .join(', ');
    }
  }

  setValidators(): void {
    if (this.showAddress) {
      this.selectedStaffForm
        ?.get('streetAddress1')
        ?.setValidators(Validators.required);
      this.selectedStaffForm?.get('streetAddress1')?.updateValueAndValidity();
    }

    this.selectedStaffForm
      ?.get('email')
      ?.setValidators(validateEmail(this.showEmail));
    this.selectedStaffForm?.get('email')?.updateValueAndValidity();

    if (this.showSchool) {
      this.selectedStaffForm?.get('school')?.setValidators(Validators.required);
      this.selectedStaffForm?.get('school')?.updateValueAndValidity();
    }

    this.selectedStaffForm
      ?.get('phone')
      ?.setValidators(validatePhoneNumberMaskedTextBox(true));
    this.selectedStaffForm?.get('phone')?.updateValueAndValidity();
  }

  clearValidators(): void {
    for (const CONTROL in this.selectedStaffForm.controls) {
      this.selectedStaffForm.controls[CONTROL].setErrors(null);
    }
  }

  ngOnInit(): void {
    if (this.initialStaff) {
      this.selectedStaff = this.initialStaff;
      this.patchSelectedStaffToForm();
      this.setValidators();
    }

    // Emit initial status
    this.selectedStaffFormStatus.emit(this.selectedStaffForm.valid);

    // Emit on every status change
    this.selectedStaffForm.statusChanges.subscribe(() => {
      this.selectedStaffFormStatus.emit(this.selectedStaffForm.valid);
    });
    this.handleMutualExclusion();
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }
  ngAfterViewInit(): void {
    if (this.router.url.includes('view')) {
      this.disableSearch = true;
      for (const CONTROL in this.selectedStaffForm.controls) {
        this.selectedStaffForm.controls[CONTROL].disable();
      }
    }
  }
  resetSearch(): void {
    this.isSearchVisible = false;
    this.selectedStaff = null;
    this.selectedStaffForm.reset();
    this.selectedStaffForm.get('phone')?.clearValidators();
    this.selectedStaffForm.get('phone')?.updateValueAndValidity();
    this.selectedStaffForm.get('email')?.clearValidators();
    this.selectedStaffForm.get('email')?.updateValueAndValidity();
    this.resetSearchChange.next(true);
  }

  isFieldMandatory(ctrl: string): boolean {
    return this.selectedStaffForm?.get(ctrl)?.hasValidator(Validators.required);
  }

  get isInvalid(): boolean {
    return this.selectedStaffForm?.invalid ?? false;
  }

  private handleMutualExclusion(): void {
    const firstName = this.staffForm.get('firstName');
    const lastName = this.staffForm.get('lastName');
    const fullName = this.staffForm.get('fullName');
    const loginId = this.staffForm.get('loginId');

    loginId?.valueChanges.subscribe(value => {
      if (value) {
        firstName?.disable({ emitEvent: false });
        lastName?.disable({ emitEvent: false });
        fullName?.disable({ emitEvent: false });
      } else {
        if (!this.disableSearch) {
          firstName?.enable({ emitEvent: false });
          lastName?.enable({ emitEvent: false });
          fullName?.enable({ emitEvent: false });
        }
      }
    });

    const toggleLoginId = () => {
      const hasName =
        firstName?.value || lastName?.value || fullName?.value;

      if (hasName) {
        loginId?.disable({ emitEvent: false });
      } else {
        if (!this.disableSearch) {
          loginId?.enable({ emitEvent: false });
        }
      }
    };

    firstName?.valueChanges.subscribe(toggleLoginId);
    lastName?.valueChanges.subscribe(toggleLoginId);
    fullName?.valueChanges.subscribe(toggleLoginId);
  }
}
